<template>
  <VueDraggableResizable
    :w="width"
    :minWidth="250"
    :h="height"
    :minHeight="minHeight"
    :z="zIndex"
    :x="xAxis"
    :y="yAxis"
    :lockAspectRatio="false"
    :handles="['br']"
    class="StickerMemo"
    :class="{ isFolded }"
    :resizable="!isFolded"
    @activated="onActive"
    @deactivated="onDeactive"
    :onDrag="onDrag"
    @dragstop="onDragStop"
    @resizestop="onResizeStop"
    :drag-cancel="'.MemoContent'"
    :parent="true"
  >
    <template #br>
      <div class="Triangle1"></div>
      <div class="Triangle2"></div>
    </template>
    <div class="StickerMemoHeader" @dblclick.stop="onDoubleClick" :class="{ isFolded }">
      <div class="MemoHeaderText gray-9--text" v-show="isFolded">{{ innerText }}</div>
      <div class="MemoHeaderBtn">
        <v-btn class="UXbtn" icon flat @click.stop="memoEvent('NEW')" @dblclick.stop :disabled="hasMaxCount"><i class="UXicon-plus"></i></v-btn>
      </div>
      <div class="MemoHeaderIcons">
        <v-btn class="UXbtn" icon flat @click.stop="memoEvent('COPY')" @dblclick.stop :disabled="hasMaxCount"
          ><i class="UXicon-copy_outlined"></i
        ></v-btn>
        <v-btn class="UXbtn" icon flat @click.stop="memoEvent('DELETE')" @dblclick.stop><i class="UXicon-close"></i></v-btn>
      </div>
    </div>
    <div class="StickerMemoMain">
      <div class="MemoContent" v-show="!isFolded" @click="onClickEditor">
        <div :contenteditable="!isLock" class="EditableDiv" @input="inputEditor" @keydown="keydownEditor" ref="editableDiv"></div>
        <span class="MemoEmptyText" v-show="noContent">메모를 작성해 주세요.</span>
      </div>
      <div class="MemoToolbar" v-show="!isFolded">
        <div style="margin-left: auto">
          <v-btn class="UXbtn small saveBtn" dark color="#5578F0" round @click="memoEvent('SAVE')" :disabled="saveDisabled">저장</v-btn>
          <v-btn flat icon @click="onClickLockBtn" class="UXbtn lockBtn" :class="{ isLock }" :disabled="noContent"><i class="UXicon-lock"></i></v-btn>
        </div>
      </div>
    </div>
  </VueDraggableResizable>
</template>

<script>
import VueDraggableResizable from 'vue-draggable-resizable';
import { isEqual } from 'lodash-es';
import DOMPurify from 'dompurify';

export default {
  name: 'StickerMemo',
  components: {
    VueDraggableResizable
  },
  props: {
    memoConfig: {
      type: Object,
      required: false,
      default: () => {}
    },
    index: {
      type: Number
    },
    hasMaxCount: {
      type: Boolean
    },
    activeMemoUniqueKey: {
      type: [Number, String]
    },
    showMemo: {
      type: Boolean
    }
  },
  data: () => {
    return {
      timer: null,
      interval: 25,
      innerHTML: '',
      innerText: '',
      initMemoContent: '',
      DOMPurifyConfig: {
        ALLOWED_TAGS: ['br', 'div'], // 허용할 태그 리스트
        ALLOW_DATA_ATTR: false, // data- 속성 허용 여부
        ALLOWED_ATTR: [] // 허용할 속성 리스트
      },
      currentWidth: 450,
      currentHeight: 300,
      updateMemoConfig: {},
      tempMemoConfig: {},
      resizeTimer: null,
      isResizeBrowser: false,
      rightGap: 30,
      bottomGap: 30
    };
  },
  computed: {
    saveDisabled() {
      if (!this.updateMemoConfig.id) {
        // 서버에 저장되지 않은 메모장은 빈 문자열 (공백만 있는 문자열도) 저장 못함
        return !this.filteredHTML.length || !this.filteredText.length;
      }

      if (this.updateMemoConfig.isCopied) {
        return false;
      }

      if (!this.updateMemoConfig.isEdited) {
        // 수정하지 않은 내용은 저장 못함
        return true;
      }
    },
    filteredText() {
      return this.innerText.replace(/[\s\n\r]/g, '');
    },
    filteredHTML() {
      return this.innerHTML.replace(/<\/?div[^>]*>|<br\s?\/?>/g, '');
    },
    getEmojiRegex() {
      return /(?:[\u2700-\u27bf]|(?:\ud83c[\udde6-\uddff]){2}|[\ud800-\udbff][\udc00-\udfff]|[\u0023-\u0039]\ufe0f?\u20e3|\u3299|\u3297|\u303d|\u3030|\u24c2|\ud83c[\udd70-\udd71]|\ud83c[\udd7e-\udd7f]|\ud83c\udd8e|\ud83c[\udd91-\udd9a]|\ud83c[\udde6-\uddff]|\ud83c[\ude01-\ude02]|\ud83c\ude1a|\ud83c\ude2f|\ud83c[\ude32-\ude3a]|\ud83c[\ude50-\ude51]|\u203c|\u2049|[\u25aa-\u25ab]|\u25b6|\u25c0|[\u25fb-\u25fe]|\u00a9|\u00ae|\u2122|\u2139|\ud83c\udc04|[\u2600-\u26FF]|\u2b05|\u2b06|\u2b07|\u2b1b|\u2b1c|\u2b50|\u2b55|\u231a|\u231b|\u2328|\u23cf|[\u23e9-\u23f3]|[\u23f8-\u23fa]|\ud83c\udccf|\u2934|\u2935|[\u2190-\u21ff]|[\u200B-\u200D\uFEFF]|\uFE0F)/g;
    },
    getUnicodeEmptySpace() {
      return /[\u2000-\u200B\u202F\u205F\u3000]+/g;
    },
    getHTMLEntityStringRegex() {
      return /&(amp|lt|gt|quot|apos|nbsp|#\d+|#x[0-9A-Fa-f]+);/g;
    },
    getHTMLEntityStringRegexByInnerHTML() {
      return /&amp;(amp|lt|gt|quot|apos|nbsp|#\d+|#x[0-9A-Fa-f]+);/g;
    },
    isLock() {
      return this.updateMemoConfig.isLock;
    },
    isActive() {
      return this.updateMemoConfig.z === 16;
    },
    isFolded() {
      return this.updateMemoConfig.isFolded;
    },
    width() {
      return this.updateMemoConfig.width;
    },
    height() {
      return this.isFolded ? 54 : this.updateMemoConfig.height;
    },
    minHeight() {
      // minHeight 옵션은 리사이징될 경우 라이브러리 내에서 최소로 줄이지 못하는 값을 넣는걸로 보여짐
      return 300;
    },
    zIndex() {
      return this.updateMemoConfig.z;
    },
    xAxis() {
      return this.isResizeBrowser ? this.tempMemoConfig.x : this.updateMemoConfig.x;
    },
    yAxis() {
      return this.isResizeBrowser ? this.tempMemoConfig.y : this.updateMemoConfig.y;
    },
    noContent() {
      return !this.updateMemoConfig.content.length;
    }
  },
  watch: {
    memoConfig: {
      immediate: true,
      handler(value) {
        // immediate 를 사용해서 created 훅에서 중복 로직 제거함
        this.updateMemoConfig = { ...value };
        // 서버에 저장 완료 이후에 항상 다시 initMemoContent 초기화
        if (value.content !== this.initMemoContent) {
          this.initMemoContent = value.content;
        }
      }
    },
    activeMemoUniqueKey: {
      immediate: true,
      handler(value) {
        // watch 훅이 위에서부터 순서대로 작동해서 updateMemoConfig 데이터 세팅된 후에 비교가능
        if (value === this.updateMemoConfig.id || value === this.updateMemoConfig.index) {
          this.updateMemoConfig.z = 16;
        } else {
          this.updateMemoConfig.z = 15;
        }
      }
    },
    showMemo(val) {
      if (!val) {
        this.reset();
      } else {
        window.addEventListener('beforeunload', this.onRefresh);
      }
    }
  },
  created() {
    this.updateMemoConfig.x = this.updateMemoConfig.id ? this.updateMemoConfig.x : this.getX();
    this.updateMemoConfig.y = this.updateMemoConfig.id ? this.updateMemoConfig.y : this.getY();

    this.currentWidth = this.updateMemoConfig.width;
    this.currentHeight = this.updateMemoConfig.height;

    this.tempMemoConfig = { x: this.updateMemoConfig.x, y: this.updateMemoConfig.y };

    if (this.updateMemoConfig.isCopied) {
      this.updateMemoConfig.isCopied = false;
      this.setTimer();
    }

    window.addEventListener('beforeunload', this.onRefresh);
    window.addEventListener('resize', this.onResize);
  },
  mounted() {
    this.$nextTick(() => {
      this.setEditableDivInnerHTML(this.getSanitizeContent(this.updateMemoConfig.content));

      this.innerText = this.$refs.editableDiv.innerText;
      this.innerHTML = this.$refs.editableDiv.innerHTML;

      // 최초 한번 실행
      this.setResizeBrowser();
    });
  },
  beforeDestroy() {
    // 메모장 on 할 경우 동작합니다 (메모리스트에 의해서 초기화 된 후 컴포넌트 재생성됨)
    // 새로고침 시에는 동작하지 않음
    this.reset();
  },
  methods: {
    getCurrentX() {
      // window 값이 캐싱된 값 리턴되어서 computed -> method 로 옮김
      // document.body.clientWidth 로 하게되면 스크롤 생긴 내부 총 영역 너비가 되어서
      // 화면에서 보이는 width가 아닌 실제 스크롤 내부의 width 로 잡힘
      return window.innerWidth - this.currentWidth;
    },
    getCurrentY() {
      // document.body.clientHeight 로 하게되면 스크롤 생긴 내부 총 영역 높이가 되어서
      // 화면에서 보이는 height가 아닌 실제 스크롤 내부의 height 로 잡힘
      return window.innerHeight - this.currentHeight;
    },
    patchPoint() {
      if (!this.updateMemoConfig.id) {
        return;
      }
      this.$http({
        url: `/api/v1/notepads/${this.updateMemoConfig.id}/point`,
        method: 'PATCH',
        data: {
          pointX: this.updateMemoConfig.x,
          pointY: this.updateMemoConfig.y,
          pointZ: this.updateMemoConfig.z
        }
      });
    },
    patchSize() {
      if (!this.updateMemoConfig.id) {
        return;
      }
      this.$http({
        url: `/api/v1/notepads/${this.updateMemoConfig.id}/size`,
        method: 'PATCH',
        data: {
          width: this.currentWidth,
          height: this.currentHeight
        }
      });
    },
    onClickEditor() {
      if (this.isLock) {
        return;
      }
      if (this.noContent) {
        this.moveCursorToEnd();
      }
    },
    onDoubleClick() {
      this.updateMemoConfig.isFolded = !this.updateMemoConfig.isFolded;

      // 폴드 되기 전에 마지막 사이즈는 currentHeight = 리사이징에서 마지막 이벤트 일어나면 변경됨
      if (this.currentHeight !== this.updateMemoConfig.height) {
        this.updateMemoConfig.height = this.currentHeight;
      }
    },
    reset() {
      this.clearTimer();
      window.removeEventListener('copy', this.onCopy);
      window.removeEventListener('beforeunload', this.onRefresh);
      window.removeEventListener('resize', this.onResize);
    },
    onClickLockBtn() {
      if (this.updateMemoConfig.id) {
        this.$http({
          url: `/api/v1/notepads/${this.updateMemoConfig.id}/lock-toggle`,
          method: 'PATCH'
        })
          .then(() => {
            this.updateMemoConfig.isLock = !this.updateMemoConfig.isLock;
          })
          .catch((err) => {
            this.$alertMsg(err.message);
          });
      } else {
        this.updateMemoConfig.isLock = !this.updateMemoConfig.isLock;
      }
    },
    getSanitizeContent(content) {
      // br, div 태그 제외하고 걸러지게 수정
      // 그 외의 XSS 스크립트 공격 보안을 위해 금지할 속성과 태그 적용
      return DOMPurify.sanitize(content, this.DOMPurifyConfig);
    },
    setEditableDivInnerHTML(newValue) {
      this.$refs.editableDiv.innerHTML = newValue;
      this.moveCursorToEnd();
    },
    setEditableDivInnerText(newValue) {
      this.$refs.editableDiv.innerText = newValue;
    },
    moveCursorToEnd() {
      const editableDiv = this.$refs.editableDiv;
      editableDiv.focus();

      // 커서를 마지막 글자 위치로 이동
      const range = document.createRange();
      const selection = window.getSelection();
      range.selectNodeContents(editableDiv);
      range.collapse(false);
      selection.removeAllRanges();
      selection.addRange(range);
    },
    setResizeBrowser() {
      if (this.updateMemoConfig.x > this.getCurrentX()) {
        this.tempMemoConfig.x = this.getCurrentX() - this.rightGap;
        this.isResizeBrowser = true;
      }

      if (this.updateMemoConfig.y > this.getCurrentY()) {
        this.tempMemoConfig.y = this.getCurrentY() - this.bottomGap;
        this.isResizeBrowser = true;
      }
    },
    onResizeBrowser() {
      if (this.updateMemoConfig.x <= this.getCurrentX()) {
        this.tempMemoConfig.x = Number(JSON.stringify(JSON.parse(this.updateMemoConfig.x)));
      } else {
        this.tempMemoConfig.x = this.getCurrentX() - this.rightGap;
      }

      if (this.updateMemoConfig.y <= this.getCurrentY()) {
        this.tempMemoConfig.y = Number(JSON.stringify(JSON.parse(this.updateMemoConfig.y)));
      } else {
        this.tempMemoConfig.y = this.getCurrentY() - this.bottomGap;
      }
    },
    onResize() {
      clearTimeout(this.resizeTimer);
      this.resizeTimer = setTimeout(() => {
        if (!this.isResizeBrowser) {
          this.setResizeBrowser();
        } else {
          this.onResizeBrowser();
        }
        this.resizeTimer = null;
      }, 2000);
    },
    onRefresh() {
      this.clearTimer();
    },
    getX() {
      if (this.index) {
        return document.body.clientWidth - 505 - this.interval * this.index;
      } else {
        return document.body.clientWidth - 505;
      }
    },
    getY() {
      if (this.index) {
        return 55 + this.interval * this.index;
      } else {
        return 55;
      }
    },
    onCopy() {
      // copy 할 경우에 innerText 는 줄바꿈 추가로 생기는 이슈 있음
      navigator.clipboard.writeText(window.getSelection().toString());
    },
    onActive() {
      this.$emit('activeMemo', this.updateMemoConfig);
      window.addEventListener('copy', this.onCopy);
      document.body.style.userSelect = 'none';
    },
    onDeactive() {
      window.removeEventListener('copy', this.onCopy);
      document.body.style.userSelect = 'auto';
    },
    onDrag(x, y) {
      return x >= 0 && y >= 0 && x <= this.getCurrentX() && y <= this.getCurrentY();
    },
    onDragStop(x, y) {
      this.updateMemoConfig.x = x;
      this.updateMemoConfig.y = y;
      this.patchPoint();
    },
    onResizeStop(x, y, w, h) {
      this.currentWidth = w;
      this.currentHeight = h;
      this.patchSize();
    },
    keydownEditor(event) {
      if (event.key === 'Backspace') {
        setTimeout(() => {
          if (['<div><br></div>', '<br>'].indexOf(this.innerHTML) !== -1) {
            this.updateMemoConfig.content = '';
            this.setEditableDivInnerText(this.updateMemoConfig.content);
            this.setEditableDivInnerHTML(this.updateMemoConfig.content);
          }
        });
      }
    },
    replaceWithRegex(regex, text, replace = '') {
      return text.replace(regex, replace);
    },
    setEditedWithTimer() {
      this.updateMemoConfig.isSaved = false;
      if (!isEqual(this.initMemoContent, this.updateMemoConfig.content)) {
        this.updateMemoConfig.isEdited = true;
        this.setTimer();
      } else {
        this.updateMemoConfig.isEdited = false;
        this.clearTimer();
      }
    },
    inputEditor(event) {
      // inner text, inner html 값
      this.innerText = event.target.innerText;
      this.innerHTML = event.target.innerHTML;

      // 이모지에 해당하는 데이터 입력 시 체크
      if (this.getEmojiRegex.test(this.innerText)) {
        this.innerText = this.replaceWithRegex(this.getEmojiRegex, this.innerText);
        this.innerText = this.replaceWithRegex(this.getUnicodeEmptySpace, this.innerText);
        this.setEditableDivInnerText(this.innerText);
        if (this.getEmojiRegex.test(this.innerHTML)) {
          this.innerHTML = this.replaceWithRegex(this.getEmojiRegex, this.innerHTML);
          this.innerHTML = this.replaceWithRegex(this.getUnicodeEmptySpace, this.innerHTML);
          this.setEditableDivInnerHTML(this.innerHTML);
        }
      }

      // HTML 엔티티 문자열에 해당하는 데이터 입력 시 체크
      if (this.getHTMLEntityStringRegex.test(this.innerText)) {
        this.innerText = this.replaceWithRegex(this.getHTMLEntityStringRegex, this.innerText);
        this.setEditableDivInnerText(this.innerText);
        if (this.getHTMLEntityStringRegex.test(this.innerHTML)) {
          this.innerHTML = this.replaceWithRegex(this.getHTMLEntityStringRegexByInnerHTML, this.innerHTML);
          this.setEditableDivInnerHTML(this.innerHTML);
        }
      }

      const newInnerHTML = JSON.parse(JSON.stringify(this.innerHTML));
      const cleanInnerHTML = this.getSanitizeContent(newInnerHTML);
      // 서버에 저장하기 전에 필터링된 데이터로 세팅
      if (!isEqual(newInnerHTML, cleanInnerHTML)) {
        this.updateMemoConfig.content = cleanInnerHTML;
        // html 필터링 된 경우 리렌더링
        this.setEditableDivInnerHTML(this.updateMemoConfig.content);
      } else {
        this.updateMemoConfig.content = newInnerHTML;
      }

      if (!this.updateMemoConfig.content) {
        this.setEditableDivInnerHTML(this.updateMemoConfig.content);
      }

      // 데이터 변경여부 변경과 함께 타이머 설정여부 (API 요청여부) 변경
      this.setEditedWithTimer();
    },
    syncWidthAndHeight() {
      if (this.updateMemoConfig.width !== this.currentWidth) {
        this.updateMemoConfig.width = this.currentWidth;
      }
      if (this.updateMemoConfig.height !== this.currentHeight) {
        this.updateMemoConfig.height = this.currentHeight;
      }
    },
    memoEvent(type) {
      switch (type) {
        case 'NEW':
          this.$emit('addMemo');
          break;
        case 'DELETE':
          this.$emit('deleteMemo', this.updateMemoConfig);
          break;
        case 'COPY':
          this.$emit('copyMemo', { ...this.updateMemoConfig, isCopied: true });
          break;
        case 'SAVE':
          if (this.timer) {
            this.clearTimer();
          }
          if (!this.updateMemoConfig.id) {
            // 타이머 실행할 경우 공백 제외한 빈 데이터인 경우, 요청 안함
            // 버튼도 비활성화 되어있어야 함
            if (!this.filteredText.length || !this.filteredHTML.length) {
              return;
            }
          } else {
            if (!this.filteredText.length || !this.filteredHTML.length) {
              this.updateMemoConfig.content = '';
            }
          }
          this.syncWidthAndHeight();
          this.updateMemoConfig.isAutoSave = false;
          this.$emit('saveMemo', this.updateMemoConfig);
          break;
      }
    },
    clearTimer() {
      if (this.timer) {
        clearTimeout(this.timer);
        this.timer = null;
      }
    },
    setTimer() {
      // 이전거 삭제 이후에
      this.clearTimer();
      // 새로 등록
      this.timer = setTimeout(() => {
        if (!this.updateMemoConfig.id) {
          // 타이머 실행할 경우 공백 제외한 빈 데이터인 경우, 요청 안함
          // 버튼도 비활성화 되어있어야 함
          if (!this.filteredText.length || !this.filteredHTML.length) {
            this.clearTimer();
            return;
          }
        } else {
          if (!this.filteredText.length || !this.filteredHTML.length) {
            this.updateMemoConfig.content = '';
          }
        }
        this.syncWidthAndHeight();
        this.updateMemoConfig.isAutoSave = true;
        this.$emit('saveMemo', this.updateMemoConfig);
        this.clearTimer();
      }, 5000);
    }
  }
};
</script>
<style lang="scss">
.StickerMemo {
  .MemoContent {
    i {
      font-size: 14px;
      line-height: 20px;
      font-style: italic;
      font-family: none;
    }
    i,
    u,
    strike {
      font-weight: inherit;
    }
  }
  &.vdr {
    touch-action: none;
    position: absolute;
    box-sizing: border-box;
  }
  .handle {
    width: 10px;
    height: 10px;
    position: absolute;
    box-sizing: border-box;
  }
  .handle-bl {
    bottom: -10px;
    left: -10px;
    cursor: sw-resize;
  }
  .handle-br {
    bottom: -10px;
    right: -10px;
    cursor: se-resize;
  }
  &.active {
    .handle {
      border: none !important;
      background: none !important;
    }
    .handle-br {
      display: flex !important;
      .Triangle1 {
        width: 0;
        height: 0;
        border-top: 8px solid $gray-7;
        border-right: 8px solid transparent;
        float: left;
      }
      .Triangle2 {
        width: 0;
        height: 0;
        border-bottom: 8px solid $gray-7;
        border-left: 8px solid transparent;
        float: left;
        margin-left: -7px;
        margin-top: 1px;
      }
    }
  }
  &:not(.active) {
    &:not(.dragging) {
      user-select: none !important;
      -webkit-user-select: none !important;
      -moz-user-select: none !important;
      -ms-user-select: none !important;
    }
  }
}
</style>
<style lang="scss" scoped>
.StickerMemo {
  position: fixed;
  border: unset;
  background: $gray-0;
  // overflow: hidden;
  border-radius: 5px;
  box-shadow: 0px 0px 20px 4px rgba(75, 70, 92, 0.3);
  &.isFolded {
    height: 54px !important;
  }
  .UXbtn {
    &.v-btn--icon {
      min-width: 24px;
      height: 24px;
      width: 24px;
    }
  }
  .StickerMemoHeader {
    display: inline-flex;
    align-items: center;
    padding: 15px 17px;
    background-color: $pb-2;
    height: 54px;
    width: 100%;
    .UXbtn {
      &.v-btn--disabled {
        opacity: 0.4;
      }
    }
    &.isFolded {
      .MemoHeaderBtn {
        display: none;
      }
      &:hover {
        .MemoHeaderText {
          display: none;
        }
        .MemoHeaderBtn {
          display: block;
        }
      }
    }
    .MemoHeaderText {
      font-size: 14px;
      line-height: 20px;
      max-width: 100%;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: normal;
      max-height: 20px;

      display: -webkit-box;
      -webkit-line-clamp: 1;
      -webkit-box-orient: vertical;
    }
    .MemoHeaderIcons {
      margin-left: auto;
      min-width: 60px;
      text-align: right;
      .UXbtn {
        .UXicon-copy_outlined {
          font-size: 18px;
        }
        &.moreBtn {
          transform: rotate(90deg);
        }
      }
    }
    .UXbtn {
      &.closeBtn {
        margin-left: 10px;
      }
    }
  }
  .StickerMemoMain {
    height: calc(100% - 54px);
    .MemoContent {
      padding: 20px 20px 0px 20px;
      background-color: #f8faff;
      width: 100%;
      height: calc(100% - 60px);
      font-size: 14px;
      line-height: 20px;
      position: relative;
      .MemoEmptyText {
        color: $gray-6;
        padding: 20px;
        position: absolute;
        top: 0;
        left: 0;
      }
      div {
        &.EditableDiv {
          color: $gray-9;
          height: 100%;
          max-height: 100%;
          overflow: auto;
          scrollbar-width: thin;
          -webkit-user-drag: auto !important;
          user-select: text !important;
          -webkit-user-select: text !important;
          -moz-user-select: text !important;
          -ms-user-select: text !important;
          &:focus-visible {
            outline: none;
          }
        }
      }
    }
    .MemoToolbar {
      background-color: #f8faff;
      padding: 20px;
      width: 100%;
      height: 60px;
      display: inline-flex;
      justify-content: space-between;
      align-items: center;
      .UXbtn {
        &.v-btn--icon {
          min-width: 20px;
          height: 20px;
          width: 20px;
          i {
            color: $gray-5;
            font-size: 20px;
          }
        }
        &.v-btn--icon {
          &.lockBtn {
            min-width: 24px;
            height: 24px;
            width: 24px;
            &.v-btn--disabled {
              opacity: 0.4;
            }
            &:hover {
              background-color: $pb_1;
              opacity: 1 !important;
              i {
                color: $pb_6;
              }
              &:before {
                opacity: 0 !important;
              }
            }
            i {
              color: $gray-5;
              font-size: 24px;
            }
            &.isLock {
              i {
                color: $pb_6;
              }
            }
          }
          &.saveBtn {
            min-width: 50px;
            width: 50px;
          }
        }
      }
    }
  }
}
</style>



// WEBPACK FOOTER //
// src/components/StickerMemo.vue